// Cross-platform IO helpers — Mac/Windows/iOS/Android safe.
// Use these everywhere we need to download text or copy to clipboard.

// True only on touch-first devices where the Web Share API actually offers
// a "Save to Files" / "Save to Downloads" path. Mac/Windows desktop browsers
// expose canShare for files but the share sheet there is AirDrop/Mail, NOT a
// download — so we MUST skip Web Share on desktop.
function isMobileTouchDevice() {
  if (typeof window === "undefined") return false;
  const ua = (navigator.userAgent || "").toLowerCase();
  const isIOS = /iphone|ipad|ipod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const isAndroid = /android/.test(ua);
  return isIOS || isAndroid;
}

export async function downloadText(filename, text) {
  const blob = new Blob([text], { type: "text/plain;charset=utf-8" });

  // 1) Web Share API — ONLY on iOS/Android (mobile share sheet has "Save to Files")
  if (isMobileTouchDevice()) {
    try {
      if (typeof File !== "undefined" && navigator?.canShare && navigator?.share) {
        const file = new File([blob], filename, { type: "text/plain" });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: filename });
          return true;
        }
      }
    } catch (_) { /* user cancelled or unsupported — fall through */ }
  }

  // 2) Standard a[download] — works on Mac/Windows/Android Chrome/Firefox/Safari
  try {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.rel = "noopener";
    // NOTE: do NOT set target — Mac Safari/Chrome navigate the page to the blob
    // when target is set, which prevents the "Save to Downloads" path.
    a.style.display = "none";
    document.body.appendChild(a);
    // Dispatch a real MouseEvent — required on Safari for programmatic downloads.
    const evt = new MouseEvent("click", {
      bubbles: true, cancelable: true, view: window, button: 0,
    });
    a.dispatchEvent(evt);
    setTimeout(() => {
      try { document.body.removeChild(a); } catch (_) {}
      try { URL.revokeObjectURL(url); } catch (_) {}
    }, 4000);
    return true;
  } catch (_) { /* fall through */ }

  // 3) Last resort — open in a new tab as a data URL so user can long-press save
  try {
    const dataUrl = `data:text/plain;charset=utf-8,${encodeURIComponent(text)}`;
    const w = window.open(dataUrl, "_blank");
    return !!w;
  } catch (_) {
    return false;
  }
}

export async function copyText(text) {
  // 1) Clipboard API — modern path
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (_) { /* fall through */ }

  // 2) Legacy execCommand — works in Safari/older browsers
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.top = "0";
    ta.style.left = "0";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    ta.setSelectionRange(0, text.length);
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch (_) {
    return false;
  }
}
